import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert
} from "@mui/material";
import { a11yProps, TabPanel } from "../ui";
import GeneralTab, { getDefaultGeneralValues } from "../customerrecord/tabs/eyeExam/GeneralTab";
import ObjectiveTab, { getDefaultObjectiveValues } from "../customerrecord/tabs/eyeExam/ObjectiveTab";
import ExternTab, { getDefaultExternValues } from "../customerrecord/tabs/eyeExam/ExternTab";
import InternTab, { getDefaultInternValues } from "../customerrecord/tabs/eyeExam/InternTab";
import RefractTab, { getDefaultRefractValues } from "../customerrecord/tabs/eyeExam/RefractTab";
import DiagnosisTab, { getDefaultDiagnosisValues } from "../customerrecord/tabs/eyeExam/DiagnosisTab";

/**
 * TemplateDataEditor
 * Dialog for editing eye exam template payload data.
 * Uses same form structure as EyeExamDialog but for template editing.
 * 
 * Props:
 * - open: boolean
 * - onClose: function
 * - template: { id, name, description, payload }
 * - onSave: function(payload) - callback with updated payload
 */
export default function TemplateDataEditor({ open, onClose, template, onSave }) {
  const [tab, setTab] = useState(0);
  const [general, setGeneral] = useState(getDefaultGeneralValues());
  const [objective, setObjective] = useState(getDefaultObjectiveValues());
  const [refract, setRefract] = useState(getDefaultRefractValues());
  const [extern, setExtern] = useState(getDefaultExternValues());
  const [intern, setIntern] = useState(getDefaultInternValues());
  const [diagnosis, setDiagnosis] = useState(getDefaultDiagnosisValues());
  const [saving, setSaving] = useState(false);

  // Load template payload when dialog opens or template changes
  useEffect(() => {
    if (open && template?.payload) {
      const p = template.payload;
      setGeneral(prev => ({ ...prev, ...(p.general || {}) }));
      setObjective(prev => ({ ...prev, ...(p.objective || {}) }));
      setRefract(prev => ({ ...prev, ...(p.refract || {}) }));
      setExtern(prev => ({ ...prev, ...(p.extern || {}) }));
      setIntern(prev => ({ ...prev, ...(p.intern || {}) }));
      setDiagnosis(prev => ({ ...prev, ...(p.diagnosis || {}) }));
    }
  }, [open, template]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setTab(0);
      setGeneral(getDefaultGeneralValues());
      setObjective(getDefaultObjectiveValues());
      setRefract(getDefaultRefractValues());
      setExtern(getDefaultExternValues());
      setIntern(getDefaultInternValues());
      setDiagnosis(getDefaultDiagnosisValues());
    }
  }, [open]);

  const handleSave = async () => {
    const payload = { general, objective, refract, extern, intern, diagnosis };
    setSaving(true);
    try {
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh'
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          Edit Template Data: {template?.name || 'Template'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {template?.description || 'No description'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          Configure the eye exam fields that will be pre-filled when this template is applied to a patient exam.
        </Alert>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label="Template data tabs">
            <Tab label="General" {...a11yProps(0)} />
            <Tab label="Objective" {...a11yProps(1)} />
            <Tab label="Refraction" {...a11yProps(2)} />
            <Tab label="External" {...a11yProps(3)} />
            <Tab label="Internal" {...a11yProps(4)} />
            <Tab label="Diagnosis" {...a11yProps(5)} />
          </Tabs>
        </Box>

        <TabPanel value={tab} index={0}>
          <GeneralTab value={general} onChange={setGeneral} />
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <ObjectiveTab value={objective} onChange={setObjective} />
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <RefractTab value={refract} onChange={setRefract} />
        </TabPanel>

        <TabPanel value={tab} index={3}>
          <ExternTab value={extern} onChange={setExtern} />
        </TabPanel>

        <TabPanel value={tab} index={4}>
          <InternTab value={intern} onChange={setIntern} />
        </TabPanel>

        <TabPanel value={tab} index={5}>
          <DiagnosisTab value={diagnosis} onChange={setDiagnosis} />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Template Data'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
